import React from 'react';
import { Box, FormControl, FormHelperText, InputLabel } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { colorPalette } from '@qualifio/ui-commons';

function RowsIcons({ rows, isSelected }: { rows: number; isSelected?: boolean }) {
  switch (rows) {
    case 1:
      return <OneRowIcon isSelected={isSelected} />;
    case 2:
      return <TwoRowsIcon isSelected={isSelected} />;
    case 3:
    default:
      return <ThreeRowsIcon isSelected={isSelected} />;
  }
}

function OneRowIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 67" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="29" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
    </svg>
  );
}

function TwoRowsIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="23" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
      <rect x="7" y="35" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
    </svg>
  );
}

function ThreeRowsIcon({ isSelected }: { isSelected?: boolean }) {
  return (
    <svg width="35" height="35" viewBox="0 0 66 68" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7" y="17" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
      <rect x="7" y="29" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
      <rect x="7" y="41" width="51" height="10" rx="3" fill={isSelected ? '#61C1DB' : '#BBBBBB'} />
    </svg>
  );
}

interface RowsSelectFieldProps<FormValue extends FieldValues> {
  name: Path<FormValue>;
  control: Control<FormValue>;
  label: string;
  defaultValue?: any;
  helperText?: string;
}

export function RowsSelectField<FormValue extends FieldValues>({
  name,
  control,
  label,
  defaultValue = 1,
  helperText,
}: RowsSelectFieldProps<FormValue>) {
  const options = [1, 2, 3];

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <FormControl error={!!error} fullWidth>
          <InputLabel
            sx={theme => ({
              marginBottom: theme.spacing(0.4),
              transform: 'none',
              position: 'relative',
              userSelect: 'inherit',
              marginTop: '10px',
            })}
          >
            {label}
          </InputLabel>

          <Box display="flex" gap={2} mt={1}>
            {options.map(option => {
              const isSelected = value === option;

              return (
                <Box
                  key={option}
                  onClick={() => onChange(option)}
                  sx={{
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: isSelected ? colorPalette.blue.main : colorPalette.grey.light,
                    borderRadius: 1,
                    padding: 1,
                    textAlign: 'center',
                    flex: 1,
                    transition: 'background-color 0.2s, border-color 0.2s',
                  }}
                >
                  <RowsIcons rows={option} isSelected={isSelected} />
                </Box>
              );
            })}
          </Box>

          {(error || helperText) && (
            <FormHelperText error>{error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
